import { AxiosRequestConfig, AxiosResponse } from 'axios';
import { FormUtil } from '../../common/FormUtil';
import API from '../../../constants/apiEndpoints';
import { msalFetch } from '../../auth/Auth-Utils';
import {
    ADD_DOCUMENT_FAILED,
    ADD_DOCUMENT_SUCCESS,
    ADD_DOCUMENT_VERSION_SUCCESS,
    EDIT_DOCUMENT_FAILED,
    EDIT_DOCUMENT_SUCCESS,
    TOGGLE_MODAL_EDIT_DOCUMENT
} from '../../../actions/actionTypes';
import { constants } from '../../../constants/constants';
import { beginAjaxCall } from '../../../actions/ajaxStatusActions';
import { endAjaxCall } from '../../../actions/ajaxStatusActions';
import { toastr } from 'react-redux-toastr';
import moment from 'moment';
import { orderBy } from 'lodash';
import { IcloudDocument, IDocumentType, Ioption } from '../../../models';

export function create(
    formValues: {
        [key: string]: any;
    },
    dispatch: Function,
    parentId: string
): Promise<any> {
    dispatch(beginAjaxCall());
    const headers = { 'content-type': 'multipart/form-data' };
    const axiosOptions: AxiosRequestConfig = {
        method: 'post',
        data: FormUtil.toFormData(formValues),
        headers
    };
    const url = API.cloudDocument.add;
    return msalFetch(url, axiosOptions)
        .then((data: AxiosResponse<any>) => {
            if (!data.data) {
                throw new Error('Missing Data');
            } else {
                if (parentId) {
                    dispatch({
                        type: ADD_DOCUMENT_VERSION_SUCCESS,
                        payload: data.data,
                        parentId
                    });
                } else {
                    dispatch({
                        type: ADD_DOCUMENT_SUCCESS,
                        payload: data.data
                    });
                }
                dispatch({ type: TOGGLE_MODAL_EDIT_DOCUMENT });
                toastr.success(
                    'Success',
                    `Created Document${parentId ? ' Version' : ''}`,
                    constants.toastrSuccess
                );
                return true;
            }
        })
        .catch(error => {
            dispatch({
                type: ADD_DOCUMENT_FAILED,
                error,
                axiosOptions
            });
            constants.handleError(error, 'Create Document');
            console.error(error);
            throw error;
        })
        .finally(() => dispatch(endAjaxCall()));
}

export function edit(
    formValues: { [key: string]: any },
    document: IcloudDocument,
    parentId: string,
    dispatch: Function
): Promise<any> {
    dispatch(beginAjaxCall());
    const axiosOptions: AxiosRequestConfig = {
        method: 'put',
        data: { ...formValues, id: document.id }
    };
    const url = `${API.cloudDocument.single}/update/${document.id}`;
    return msalFetch(url, axiosOptions)
        .then((data: AxiosResponse<any>) => {
            if (data.status !== 200) {
                throw new Error('Missing Data');
            } else {
                dispatch({
                    type: EDIT_DOCUMENT_SUCCESS,
                    payload: { ...data.data },
                    parentId
                });
                dispatch({ type: TOGGLE_MODAL_EDIT_DOCUMENT });
                toastr.success(
                    'Success',
                    `Update Document.`,
                    constants.toastrSuccess
                );
            }
            return true;
        })
        .catch(error => {
            dispatch({
                type: EDIT_DOCUMENT_FAILED,
                error,
                axiosOptions
            });
            constants.handleError(error, 'Update Document');
            console.error(error);
            throw error;
        })
        .finally(() => dispatch(endAjaxCall()));
}

export const getLastModifiedDate = ({
    updateDate,
    createDate
}: IcloudDocument) => (updateDate ? updateDate : createDate);

// Drop a trailing "Report"/"Reports" or a plural "s" from a document type name
// so it composes cleanly into the display name (e.g. "Annual Inspection
// Reports" -> "Annual Inspection", "Drawings" -> "Drawing").
const stripReportSuffix = (label: string): string => {
    const withoutReport = label.replace(/\s+reports?$/i, '');
    if (withoutReport !== label) {
        return withoutReport.trim();
    }
    return label.replace(/s$/i, '').trim();
};

// Human-readable label for the Name column, derived from the document's type
// and date instead of the raw stored filename:
//   "[Document Type] Report [dd-MMM-yy]"  e.g. "Annual Inspection Report 09-Apr-26"
// Date prefers documentDate, falling back to the last-modified date.
// documentDate is persisted as a UTC ISO string (see EditDocumentLibraryForm),
// so it is formatted in UTC to show the calendar date the user picked rather
// than shifting a day in the viewer's timezone.
export const getDocumentDisplayName = (
    doc: IcloudDocument,
    documentTypeOptions: { [key: string]: IDocumentType }
): string => {
    const typeEntry =
        documentTypeOptions && documentTypeOptions[doc.cloudDocumentTypeID];
    const typeLabel = typeEntry ? stripReportSuffix(typeEntry.name) : '';
    const dateSource = doc.documentDate || getLastModifiedDate(doc);
    const dateLabel = dateSource
        ? moment.utc(dateSource).format('DD-MMM-YY')
        : '';
    return `${typeLabel} Report ${dateLabel}`.replace(/\s+/g, ' ').trim();
};

// Newest-first: by stored version number when the chain is numbered (dates are
// unreliable there - metadata edits rewrite updateDate chain-wide), else by date
export const sortVersionsNewestFirst = (
    versions: IcloudDocument[]
): IcloudDocument[] => {
    const isNumbered = versions.some(
        v => v.versionNumber !== undefined && v.versionNumber !== null
    );
    return orderBy(
        versions,
        isNumbered
            ? (v: IcloudDocument) => v.versionNumber || 0
            : (v: IcloudDocument) => moment.utc(getLastModifiedDate(v)).unix(),
        'desc'
    );
};

// Newest version (undefined when a doc has no versions)
export const getLatestVersion = (
    doc: IcloudDocument
): IcloudDocument | undefined => {
    if (!doc.versions || doc.versions.length === 0) {
        return undefined;
    }
    return sortVersionsNewestFirst(doc.versions)[0];
};

// Raw stored document name - what the Name column showed before MMG-4028
// replaced it with the derived "[Type] Report [date]" label. Surfaced on its
// own "Document Description" column. A document reports its latest version's
// name, falling back to its own.
export const getDocumentRawName = (doc: IcloudDocument): string => {
    const latest = getLatestVersion(doc);
    return (latest && latest.name) || doc.name || '';
};

// A document displays its latest version's mime type, falling back to its own
export const getDocumentMimeType = (doc: IcloudDocument): string => {
    const latest = getLatestVersion(doc);
    return (latest && latest.mimeType) || doc.mimeType;
};

// Stored (server-assigned) number when the chain is numbered, else positional fallback
export const getVersionDisplayNumber = (
    version: IcloudDocument,
    positionalFallback: number
): number =>
    version.versionNumber !== undefined && version.versionNumber !== null
        ? version.versionNumber
        : positionalFallback;

export const getDocumentDisplayVersion = (doc: IcloudDocument): number => {
    const latest = getLatestVersion(doc) || doc;
    const positional = (doc.versions || []).length || 1;
    return getVersionDisplayNumber(latest, positional);
};

const mimeTypeLabels: { [mimeType: string]: string } = {
    'application/pdf': 'PDF',
    'application/msword': 'DOC',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        'DOCX',
    'application/vnd.ms-excel': 'XLS',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
    'application/vnd.ms-powerpoint': 'PPT',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation':
        'PPTX',
    'image/jpeg': 'JPG',
    'image/png': 'PNG',
    'image/gif': 'GIF',
    'image/bmp': 'BMP',
    'image/tiff': 'TIFF',
    'text/plain': 'TXT',
    'text/csv': 'CSV'
};

// Short file-type label (PDF, DOCX, ...) for a document's mime type
export const getFileTypeLabel = (mimeType?: string): string => {
    if (!mimeType) {
        return '';
    }
    const label = mimeTypeLabels[mimeType.toLowerCase()];
    if (label) {
        return label;
    }
    const subtype = mimeType.split('/')[1];
    return subtype ? subtype.toUpperCase() : '';
};

// FontAwesome (far) icon name for a document's mime type
export const getFileTypeIcon = (mimeType?: string): string => {
    if (!mimeType) {
        return 'file';
    }
    const mime = mimeType.toLowerCase();
    if (mime === 'application/pdf') {
        return 'file-pdf';
    }
    if (mime === 'application/msword' || mime.includes('wordprocessingml')) {
        return 'file-word';
    }
    if (mime === 'application/vnd.ms-excel' || mime.includes('spreadsheetml')) {
        return 'file-excel';
    }
    if (mime === 'text/csv') {
        return 'file-csv';
    }
    if (
        mime === 'application/vnd.ms-powerpoint' ||
        mime.includes('presentationml')
    ) {
        return 'file-powerpoint';
    }
    if (mime.indexOf('image/') === 0) {
        return 'file-image';
    }
    if (mime.indexOf('text/') === 0) {
        return 'file-alt';
    }
    return 'file';
};

// The Add Document form's Facility dropdown opens on the facility the user is
// already browsing. Returns '' (react-reactive-form's empty-select value) when
// there is nothing valid to preselect, so the user picks explicitly rather than
// having a facility guessed for them.
export const resolveFacilityOption = (
    facilityOptions: Ioption[],
    selectedFacilityID: string
): Ioption | '' => {
    if (!selectedFacilityID) {
        return '';
    }
    return (
        facilityOptions.find(option => option.value === selectedFacilityID) ||
        ''
    );
};

// Which facility the Add Document form's Facility control should show. Three
// sources in precedence order, because a changed fieldConfig makes
// FormGenerator rebuild the whole form from formState and discard user input:
//   1. the live pre-rebuild form value - survives any rebuild trigger
//   2. formValues - set by handleCreateType before its own rebuild
//   3. the facility the user is browsing - the default on a fresh open
// Without (1) and (2) the control silently snaps back to the browsed facility
// mid-form and the document gets misfiled with no error shown.
export const resolveFacilityFormState = (
    liveFacilityOption: Ioption | undefined,
    formValues: { [key: string]: any },
    facilityOptions: Ioption[],
    selectedFacilityID: string
): Ioption | '' =>
    liveFacilityOption ||
    formValues.facilityID ||
    resolveFacilityOption(facilityOptions, selectedFacilityID);

// A document is global - it belongs to every facility in its country rather
// than to one facility - when it carries a countryID. This mirrors the API,
// which uses the same test to decide how to authorize a download:
// CloudDocumentController does `var isGlobalDocument = document.CountryID != null`
// and then gates on the user's countries instead of their facilities.
export const isGlobalDocument = (doc?: IcloudDocument): boolean =>
    Boolean(doc && doc.countryID);

// Text for the Documents table's Facility column. A global document has no one
// facility, so naming one would be misleading. An em dash rather than a hyphen:
// a lone hyphen in a wide column reads as an empty cell.
export const GLOBAL_DOCUMENT_FACILITY_LABEL = '—';

export const resolveFacilityColumnValue = (doc?: IcloudDocument): string => {
    if (isGlobalDocument(doc)) {
        return GLOBAL_DOCUMENT_FACILITY_LABEL;
    }
    return (doc && doc.facilityName) || '';
};

// Flattens a clearable react-select value to a plain id for the save payload.
//
// react-select hands back an option object for a selection, null when the user
// clears the field, and the control's raw initial value (a bare id string) when
// nothing has been touched. FormUtil.toFormData does
// `value && value.value ? value.value : value`, and FormData.append coerces
// null to the *string* "null" - so an unflattened cleared field would persist
// "null" as the id. Returns '' for every empty case instead.
export const resolveSelectValue = (value: any): string => {
    if (!value) {
        return '';
    }
    if (typeof value === 'string') {
        return value;
    }
    return value.value || '';
};

// The Add Document form's Date control defaults to today when nothing is
// carried over, so the common case needs no interaction.
//
// UTC midnight, deliberately. FormUtil.Datetime renders react-datetime with
// utc={true}, handleSubmit stores moment.utc(value).toISOString(), and
// getDocumentDisplayName formats with moment.utc(). A local moment() would be
// converted on save and could land on a different calendar day than the picker
// highlights as today - moment() late in the day at a negative offset rolls
// forward, local midnight at a large positive offset rolls back. Matching the
// picker's own UTC frame is what keeps the default, the stored value, and the
// displayed label on the same date for every user.
export const resolveDocumentDateFormState = (documentDate: string): any =>
    documentDate || moment.utc().startOf('day');

// Which facility a save is filed against. Add and Edit both have a Facility
// control and send whatever the user chose; New Version has none and inherits
// the facility being browsed. Keyed on whether the control exists rather than on
// which form it is - passing the browsed facility from a form that *does* have a
// control would silently move the document.
//
// Throws instead of falling back, because a missing option on a form that has
// the control means the config is broken, and quietly substituting the browsed
// facility would misfile the document.
export const resolveSubmitFacilityID = (
    hasFacilityControl: boolean,
    formValue: { [key: string]: any },
    selectedFacilityID: string
): string => {
    if (!hasFacilityControl) {
        return selectedFacilityID;
    }
    const facilityOption = formValue.facilityID;
    if (!facilityOption || !facilityOption.value) {
        throw new Error(
            'resolveSubmitFacilityID: the form is missing its required facilityID option'
        );
    }
    return facilityOption.value;
};
